import axios, { AxiosError } from 'axios';
import { flow, Instance, types } from 'mobx-state-tree';

import endpoints from '@configs/endpoints';
import { Partner } from '@constants/paymentLink';
import { IPayeeInfo, IPayerAddress } from '@containers/checkout/payment-form/types';
import Common from '@store/common';
import { generateDeviceId } from '@utils/deviceId';
import getCardInfo from '@utils/getCardInfo';
import { getConfirmationUrl } from '@utils/getUrlParam';

export interface FormPayload extends IPayeeInfo, IPayerAddress {
  cardNumber: string;
  cvv2: string;
  expiration: string;
  holderName: string;
}

export interface PayPayload extends FormPayload {
  paymentLinkId: string;
}

const Blumon = types
  .compose(
    Common,
    types.model({
      flags: types.optional(
        types.model({
          isSendingPayment: false,
        }),
        {},
      ),
    }),
  )
  .volatile(() => ({
    deviceIdRecord: '',
  }))
  .views((self) => ({
    get isProcessing() {
      return self.flags.isSendingPayment;
    },
    get deviceId() {
      if (self.deviceIdRecord) return self.deviceIdRecord;

      const deviceId = `${generateDeviceId()}`;
      if (deviceId) {
        self.updateField('deviceIdRecord', deviceId);
      }

      return deviceId;
    },
  }))
  .actions((self) => ({
    getPayloadDetails: (parameters: PayPayload) => {
      const [month, year] = parameters.expiration.split('/');
      const cardData = {
        number: parameters.cardNumber.replaceAll(' ', ''),
        holderName: parameters.holderName,
        expirationMonth: month,
        expirationYear: year,
        cvv: parameters.cvv2,
      };

      const paymentLinkInfo = {
        payeeFirstName: parameters.payeeFirstName,
        payeeLastName: parameters.payeeLastName,
        payeeEmail: parameters.payeeEmail.trim(),
        payeePhone: parameters.payeePhone.replaceAll(' ', ''),
        paymentLinkId: parameters.paymentLinkId,
        partner: Partner.BLUMON,
      };

      const { bin, last4 } = getCardInfo(cardData.number);
      const cardInfo = {
        bin,
        last4,
        ...cardData,
      };

      const addressDetails = {
        address: parameters.payerAddress,
        city: parameters.payerCity,
        region: parameters.payerState,
        country: parameters.payerCountry,
        zipCode: parameters.payerZipCode,
      };

      return {
        ...paymentLinkInfo,
        addressDetails,
        cardInfo,
        deviceId: self.deviceId,
        confirmationUrl: getConfirmationUrl(parameters.paymentLinkId, false),
      };
    },
  }))
  .actions((self) => ({
    pay: flow(function* (parameters: PayPayload) {
      self.flags.isSendingPayment = true;
      try {
        const payload = self.getPayloadDetails(parameters);
        const { data } = yield axios.post(endpoints.processCardPayment, payload);
        return data;
      } catch (error) {
        const { response } = error as AxiosError;
        throw new Error(response?.data.msg || (error as { msg: string }).msg);
      } finally {
        self.flags.isSendingPayment = false;
      }
    }),
  }));

export interface IBlumonStore extends Instance<typeof Blumon> {}
export default Blumon;
